import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  isActive,
  RouteReuseStrategy,
  Router,
} from '@angular/router';
import { Observable, map } from 'rxjs';
import { environment } from '../../environments/environment';
import {
  ApiPaginatedResponse,
  ApiResponse,
  Page,
} from '../pages/opportunities/models/opportunity.model';

export interface StaffNotification {
  id: string;
  recipientUserId: string;
  eventCode: string;
  title: string;
  message: string;
  context?: Record<string, unknown>;
  readAt?: string;
  createdAt: string;
}

export interface ListNotificationsQuery {
  page?: number;
  limit?: number;
  unreadOnly?: boolean;
}

const DEFAULT_NOTIFICATION_CASE = '/loan-application';

function caseRoutes(base: string, eventCodes: readonly string[]): Record<string, string> {
  return Object.fromEntries(eventCodes.map((eventCode) => [eventCode, base]));
}

const NOTIFICATION_CASE_BASE: Record<string, string> = {
  ...caseRoutes('/fee-checker', [
    'fee_payment.submitted',
    'fee_payment.resubmitted',
    'fee_payment.upi_verified',
  ]),
  ...caseRoutes('/fee-maker', ['fee_payment.approved', 'fee_payment.sent_back']),
  ...caseRoutes('/quality-check-cpa', ['fee_payment.ready_for_qc']),
  ...caseRoutes('/initiation', ['verification_assigned']),
  ...caseRoutes('/capturing', ['verification_correction']),
  ...caseRoutes('/completion', [
    'verification_completed',
    'capturing_submitted_for_completion',
  ]),
};

export function notificationRoute(notification: StaffNotification): string | null {
  const applicationId = notification.context?.['applicationId'];
  const id = typeof applicationId === 'string' ? applicationId.trim() : '';
  if (!id) {
    return null;
  }
  const base = NOTIFICATION_CASE_BASE[notification.eventCode] ?? DEFAULT_NOTIFICATION_CASE;
  return `${base}/${id}`;
}

function routeApplicationId(route: ActivatedRouteSnapshot | null): string | null {
  while (route) {
    const id = route.paramMap.get('id');
    if (id) {
      return id;
    }
    route = route.parent;
  }
  return null;
}

export function openStaffNotificationCase(
  router: Router,
  notification: StaffNotification,
  strategy: RouteReuseStrategy,
): void {
  const path = notificationRoute(notification);
  if (
    !path ||
    isActive(path, router, {
      paths: 'subset',
      queryParams: 'ignored',
      fragment: 'ignored',
      matrixParams: 'ignored',
    })()
  ) {
    return;
  }

  const reuseRoute = strategy.shouldReuseRoute;
  strategy.shouldReuseRoute = (future, curr) => {
    const futureId = routeApplicationId(future);
    const currId = routeApplicationId(curr);
    if (futureId && currId && futureId !== currId) {
      return false;
    }
    return reuseRoute.call(strategy, future, curr);
  };

  void router.navigateByUrl(path).finally(() => {
    strategy.shouldReuseRoute = reuseRoute;
  });
}

@Injectable({ providedIn: 'root' })
export class NotificationsService {
  private readonly baseUrl = `${environment.apiUrl}/notifications`;

  constructor(private readonly http: HttpClient) {}

  list(query: ListNotificationsQuery = {}): Observable<Page<StaffNotification>> {
    let params = new HttpParams();
    if (query.page != null) {
      params = params.set('page', String(query.page));
    }
    if (query.limit != null) {
      params = params.set('limit', String(query.limit));
    }
    if (query.unreadOnly === true) {
      params = params.set('unreadOnly', 'true');
    }

    return this.http
      .get<ApiPaginatedResponse<StaffNotification>>(this.baseUrl, { params })
      .pipe(
        map((res) => ({
          items: res.data ?? [],
          total: res.metadata?.total ?? res.data?.length ?? 0,
          page: res.metadata?.page ?? 1,
          limit: res.metadata?.limit ?? res.data?.length ?? 0,
        })),
      );
  }

  unreadCount(): Observable<number> {
    return this.http
      .get<ApiResponse<{ unread: number }>>(`${this.baseUrl}/unread-count`)
      .pipe(map((res) => res.data?.unread ?? 0));
  }

  markRead(id: string): Observable<StaffNotification> {
    return this.http
      .post<ApiResponse<StaffNotification>>(`${this.baseUrl}/${id}/read`, {})
      .pipe(map((res) => res.data));
  }

  markAllRead(): Observable<number> {
    return this.http
      .post<ApiResponse<{ updated: number }>>(`${this.baseUrl}/read-all`, {})
      .pipe(map((res) => res.data?.updated ?? 0));
  }
}
